import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/contexts/StoreContext';
import { walletAPI, promotionsAPI, authAPI } from '@/lib/api';
import { formatPoints, formatDate, calculateProgress, getLevelColor, theme } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import {
  Gift,
  QrCode,
  Star,
  ChevronUp,
  Store,
  User,
  LogOut,
  Sun,
  Moon,
  History,
  Trophy,
  Sparkles,
  Clock,
  Check,
  Lock,
  X
} from 'lucide-react';
import CustomerBannerCarousel from '@/components/CustomerBannerCarousel';

// Store Selector Component
function StoreSelector({ onSelect }) {
  const { stores, loading } = useStore();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-2xl mx-auto">
        <h1 className="font-outfit text-2xl font-bold mb-2 text-foreground">Elige tu tienda</h1>
        <p className="text-muted-foreground mb-6">Selecciona la tienda donde quieres acumular puntos</p>
        
        <div className="space-y-4">
          {stores.length === 0 ? (
            <Card className="glass border-white/10">
              <CardContent className="p-8 text-center">
                <Store className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No hay tiendas disponibles</p>
              </CardContent>
            </Card>
          ) : (
            stores.map((store) => (
              <Card
                key={store.id}
                className="glass border-white/10 cursor-pointer card-hover"
                onClick={() => onSelect(store)}
                data-testid={`store-card-${store.id}`}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-indigo-600/20 flex items-center justify-center">
                    <Store className="w-8 h-8 text-indigo-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-outfit font-semibold text-foreground">{store.name}</h3>
                    <p className="text-sm text-muted-foreground">{store.description || 'Programa de fidelización'}</p>
                  </div>
                  <ChevronUp className="w-5 h-5 text-muted-foreground rotate-90" />
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Main Customer Home
function CustomerHome() {
  const { user, logout } = useAuth();
  const { currentStore, branding, clearStore } = useStore();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [qrToken, setQrToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [isDark, setIsDark] = useState(theme.get() === 'dark');

  useEffect(() => {
    if (currentStore) {
      loadData();
    }
  }, [currentStore]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [walletRes, promosRes] = await Promise.all([
        walletAPI.get(currentStore.id),
        promotionsAPI.list(currentStore.id),
      ]);
      setWallet(walletRes.data);
      setPromotions(promosRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const generateQR = async () => {
    try {
      const response = await authAPI.generateQRToken();
      setQrToken(response.data.qr_token);
      setShowQR(true);
    } catch (err) {
      toast.error('Error al generar QR');
    }
  };

  const toggleTheme = () => {
    const newTheme = theme.toggle();
    setIsDark(newTheme === 'dark');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  const levelProgress = wallet?.next_level
    ? calculateProgress(
        wallet.membership.total_points - (wallet.current_level?.min_points || 0),
        wallet.next_level.min_points - (wallet.current_level?.min_points || 0)
      )
    : 100;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 glass px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="w-10 h-10 rounded-xl object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-indigo-600/20 flex items-center justify-center">
                <Gift className="w-5 h-5 text-indigo-400" />
              </div>
            )}
            <div>
              <h1 className="font-outfit font-bold text-foreground">{currentStore.name}</h1>
              <p className="text-xs text-muted-foreground">{user?.full_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-muted-foreground"
              data-testid="theme-toggle-btn"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={clearStore}
              className="text-muted-foreground"
              data-testid="change-store-btn"
            >
              <Store className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-muted-foreground"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Promotional Banner Carousel */}
      <CustomerBannerCarousel />

      {/* Hero Section */}
      <section className="relative px-4 py-8">
        <div className="glass-card p-6 rounded-2xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Tus puntos</p>
              <p className="font-outfit text-4xl font-bold text-foreground">
                {formatPoints(wallet?.membership?.total_points || 0)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Nivel</p>
              <Badge className={`${getLevelColor(wallet?.current_level?.name)} bg-amber-600/20 text-amber-400 border-0`}>
                <Trophy className="w-3 h-3 mr-1" />
                {wallet?.current_level?.name || 'Nuevo'}
              </Badge>
            </div>
          </div>

          {/* Level Progress */}
          {wallet?.next_level && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{wallet.current_level?.name || 'Inicio'}</span>
                <span>{wallet.next_level.name}</span>
              </div>
              <Progress value={levelProgress} className="h-2" />
              <p className="text-xs text-center text-muted-foreground">
                {wallet.points_to_next_level} puntos para subir de nivel
              </p>
            </div>
          )}

          {/* QR Button */}
          <Button
            onClick={generateQR}
            className="w-full mt-6 btn-premium bg-indigo-600 hover:bg-indigo-500"
            data-testid="show-qr-btn"
          >
            <QrCode className="w-5 h-5 mr-2" />
            Mostrar mi QR
          </Button>
        </div>
      </section>

      {/* Promotions */}
      <section className="px-4 mb-8">
        <h2 className="font-outfit text-lg font-semibold mb-4 text-foreground">Promociones Activas</h2>
        {promotions.length === 0 ? (
          <Card className="glass border-white/10">
            <CardContent className="p-6 text-center">
              <Sparkles className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">No hay promociones activas</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {promotions.slice(0, 3).map((promo) => (
              <Card key={promo.id} className="glass border-white/10 overflow-hidden" data-testid={`promo-card-${promo.id}`}>
                {promo.image_url && (
                  <img src={promo.image_url} alt={promo.title} className="w-full h-32 object-cover" />
                )}
                <CardContent className="p-4">
                  <h3 className="font-outfit font-semibold text-foreground">{promo.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{promo.description}</p>
                  <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    <span>Hasta {formatDate(promo.end_date)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* QR Modal */}
      {showQR && qrToken && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="glass-card p-6 rounded-2xl max-w-sm w-full text-center">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4"
              onClick={() => setShowQR(false)}
              data-testid="close-qr-btn"
            >
              <X className="w-5 h-5" />
            </Button>
            <h3 className="font-outfit text-xl font-bold mb-2 text-foreground">Tu código QR</h3>
            <p className="text-sm text-muted-foreground mb-6">Muéstralo al empleado para registrar tu visita</p>
            <div className="qr-container mx-auto mb-6">
              <QRCode value={qrToken} size={200} />
            </div>
            <p className="text-xs text-muted-foreground">Válido por 5 minutos</p>
          </div>
        </div>
      )}

      {/* Wallet Bottom Sheet */}
      <Drawer open={walletOpen} onOpenChange={setWalletOpen}>
        <DrawerTrigger asChild>
          <div className="fixed bottom-0 left-0 right-0 z-30">
            <div 
              className="glass-panel rounded-t-3xl cursor-pointer"
              onClick={() => setWalletOpen(true)}
              data-testid="wallet-trigger"
            >
              <div className="wallet-sheet-handle mx-auto" />
              <div className="px-6 pb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-600/20 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Mi Wallet</p>
                    <p className="font-outfit font-bold text-foreground">{formatPoints(wallet?.membership?.total_points || 0)}</p>
                  </div>
                </div>
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              </div>
            </div>
          </div>
        </DrawerTrigger>
        <DrawerContent className="glass-panel border-t border-white/10">
          <div className="p-6 max-h-[80vh] overflow-y-auto">
            <div className="wallet-sheet-handle mx-auto mb-6" />
            
            {/* Points Summary */}
            <div className="text-center mb-8">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Total de Puntos</p>
              <p className="font-outfit text-5xl font-bold text-foreground">{formatPoints(wallet?.membership?.total_points || 0)}</p>
              <Badge className={`mt-2 ${getLevelColor(wallet?.current_level?.name)} bg-amber-600/20 text-amber-400 border-0`}>
                <Trophy className="w-3 h-3 mr-1" />
                {wallet?.current_level?.name || 'Nuevo'}
              </Badge>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="glass rounded-xl p-4 text-center">
                <p className="text-2xl font-outfit font-bold text-foreground">{wallet?.membership?.total_visits || 0}</p>
                <p className="text-xs text-muted-foreground">Visitas totales</p>
              </div>
              <div className="glass rounded-xl p-4 text-center">
                <p className="text-2xl font-outfit font-bold text-foreground">{wallet?.redemptions?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Premios canjeados</p>
              </div>
            </div>

            {/* Available Rewards */}
            <div className="mb-6">
              <h3 className="font-outfit font-semibold mb-4 text-foreground">Premios Disponibles</h3>
              {wallet?.available_rewards?.length === 0 ? (
                <Card className="glass border-white/10">
                  <CardContent className="p-4 text-center">
                    <Lock className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">No hay premios en tu nivel actual</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {wallet?.available_rewards?.map((reward) => (
                    <Card key={reward.id} className="glass border-white/10" data-testid={`reward-card-${reward.id}`}>
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center">
                          <Gift className="w-6 h-6 text-indigo-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-foreground">{reward.name}</h4>
                          <p className="text-sm text-muted-foreground">{formatPoints(reward.points_cost)}</p>
                        </div>
                        <Badge variant="outline" className="border-amber-400/50 text-amber-400">
                          Disponible
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Redemptions */}
            <div>
              <h3 className="font-outfit font-semibold mb-4 text-foreground">Últimos Canjes</h3>
              {wallet?.redemptions?.length === 0 ? (
                <Card className="glass border-white/10">
                  <CardContent className="p-4 text-center">
                    <History className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Sin canjes aún</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {wallet?.redemptions?.slice(0, 5).map((redemption) => (
                    <Card key={redemption.id} className="glass border-white/10">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-600/20 flex items-center justify-center">
                          <Check className="w-5 h-5 text-green-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-foreground">{redemption.reward_name}</h4>
                          <p className="text-xs text-muted-foreground">{formatDate(redemption.created_at)}</p>
                        </div>
                        <span className="text-sm text-muted-foreground">-{formatPoints(redemption.points_spent)}</span>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

// Customer App Router
export default function CustomerApp() {
  const { currentStore, selectStore } = useStore();

  if (!currentStore) {
    return <StoreSelector onSelect={selectStore} />;
  }

  return (
    <Routes>
      <Route path="/" element={<CustomerHome />} />
      <Route path="*" element={<CustomerHome />} />
    </Routes>
  );
}
